/**
 * Role choices for the company dialogs (SC-439). Roles are named by their key; the name people
 * see is `Role.name` from the roles API, because a platform admin can rename any role, system
 * roles included. Nothing here may turn a key into a display name.
 *
 * Cloud accepts, on an invite or a role change, a role key (`member`, `admin`, or a custom role's
 * key) and still the legacy strings (`user`, `moderator`, `manager`, `admin`). Member is sent as
 * the key `member`, never as `user`: on a row or a default whose role is custom, cloud reads `user`
 * as an old bundle sending back the mirror it was given and changes nothing, so a move to Member
 * sent as `user` would report success and write nothing. Cloud must be live before this client.
 */
import { OWNER_ROLE_KEY } from '../../../../../types/rbac/catalogue'
import { canAssignRole } from '../../../../../types/rbac/evaluate'
import { CompanyUserRole } from '../../../core/models/Company'
import type { RbacRole, RbacSystemRole } from '../../../services/rbacService'

/** The legacy strings the cloud accepts on an invite (`INVITABLE_COMPANY_ROLES`). */
const LEGACY_INVITE_ROLES = new Set<string>([
  CompanyUserRole.USER,
  CompanyUserRole.MODERATOR,
  CompanyUserRole.MANAGER,
  CompanyUserRole.ADMIN,
])

const MEMBER_KEY = 'member'
const ADMIN_KEY = 'admin'

/** The role key a legacy role string stands for, or undefined for a custom key. */
function systemKeyOf(role: string | undefined | null): string | undefined {
  if (role === CompanyUserRole.USER || role === CompanyUserRole.MODERATOR || role === MEMBER_KEY) {
    return MEMBER_KEY
  }
  if (role === CompanyUserRole.ADMIN || role === CompanyUserRole.MANAGER) return ADMIN_KEY
  return undefined
}

/** What a role value (legacy string or key) is, as a key: `manager` is Admin, `user` is Member. */
export function roleKeyOf(role: string | undefined | null): string {
  if (!role) return MEMBER_KEY
  return systemKeyOf(role) ?? role
}

/** The value an invite or a role change carries for a role key: the key itself. */
export function roleSendValue(roleKey: string): string {
  return roleKey
}

/**
 * The company's default role key: the roles API's `defaultRoleKey` (company default, then
 * platform default, resolved by the server) when it came; else what the company settings hold
 * (`default_role_key`, which a legacy Admin string in `default_role` overrides over a custom key,
 * as cloud reads it); else the legacy string's key; else Member.
 */
export function effectiveDefaultRoleKey(input: {
  defaultRoleKey?: string | null
  /** `Company.settings.default_role_key`, when the caller has it. */
  settingsRoleKey?: string | null
  /** `Company.settings.default_role` (the legacy string), when the caller has it. */
  legacyRole?: string | null
  /** A company object to read the two settings above from, for what is not given. */
  company?: {
    default_role?: unknown
    settings?: { default_role?: unknown; default_role_key?: unknown } | null
  } | null
}): string {
  if (input.defaultRoleKey) return input.defaultRoleKey
  const legacyKey = systemKeyOf(input.legacyRole ?? companyDefaultRole(input.company))
  const fromCompany = input.company?.settings?.default_role_key
  const stored = input.settingsRoleKey ?? (typeof fromCompany === 'string' ? fromCompany : '')
  if (stored && stored !== OWNER_ROLE_KEY) {
    // Cloud reads a known legacy string in `default_role` before `default_role_key`, so over a
    // system key it always wins; over a custom key only an Admin string does (`user` is the
    // mirror every custom default carries).
    if (systemKeyOf(stored)) return legacyKey ?? stored
    return legacyKey === ADMIN_KEY ? ADMIN_KEY : stored
  }
  return legacyKey ?? MEMBER_KEY
}

/**
 * The role key of a company member row. Cloud sends `roleKey` on each row; an old cloud does
 * not, and then the legacy `role` says it: `manager` and `admin` are Admin, anything else Member.
 */
export function memberRoleKey(
  member: { role?: string | null; roleKey?: string | null } | null | undefined
): string {
  return member?.roleKey || roleKeyOf(member?.role)
}

/**
 * True when cloud says the row's role is the fallback, not one stored for the person
 * (`role_is_default`): the member has no role of their own yet.
 */
export function memberHasNoRole(
  member: { role_is_default?: boolean | null } | null | undefined
): boolean {
  return member?.role_is_default === true
}

/**
 * The value an Edit User form holds for a member, in the form a save sends: a custom role's key
 * (or `owner`, so the picker can show it) as it is, Member and Admin as the legacy string cloud
 * stored. The stored string is kept when it already stands for the key (a `manager` stays
 * `manager`), so a save that changes nothing sends no role.
 *
 * A member with no role of their own opens on the role that applies to them (cloud's
 * `effective_role_key`, else the existing logic). That key is sent as is, and `userEditChanges`
 * always sends it for such a member, so saving gives them the role explicitly (SC-439, phase F).
 */
export function memberRoleValue(
  member:
    | {
        role?: string | null
        roleKey?: string | null
        role_is_default?: boolean | null
        effective_role_key?: string | null
      }
    | null
    | undefined
): string {
  if (memberHasNoRole(member) && member?.effective_role_key) return member.effective_role_key
  const key = memberRoleKey(member)
  if (!systemKeyOf(key)) return key
  return member?.role && systemKeyOf(member.role) === key ? member.role : roleSendValue(key)
}

/** The name the roles API gives a role key, or undefined while it is unknown. */
export function roleNameOf(
  roles: readonly Pick<RbacRole, 'key' | 'name'>[],
  roleKey: string | undefined | null
) {
  return roleKey ? roles.find((r) => r.key === roleKey)?.name : undefined
}

/** What a row shows when nothing names its role at all: the built-in default role. */
export const LAST_RESORT_ROLE_LABEL = 'Member'
/** Appended when cloud says the row's role is the fallback, not one stored for the person. */
export const DEFAULT_ROLE_MARKER = '(default)'

/**
 * The name a roster or the profile menu shows for a member's role. Never blank: every person has
 * a role, a person without a usable one falls back to the default role (SC-439). A custom role is
 * stored with the legacy string `user`, so that string can't name it. In order:
 * 1. `role_name`, the Role's current name, which cloud puts on the rows it already returns (for a
 *    row whose stored role is missing, the fallback role's name);
 * 2. the name of `roleKey` among roles the screen already holds (never fetched for this);
 * 3. `fallback`, the label the screen showed before names were sent. An old cloud sends neither
 *    field, so its rows keep that label (the roles are not consulted without a `roleKey`);
 * 4. "Member".
 * A row cloud marks `role_is_default` gets "(default)" after the name. The Edit User form opens on
 * that same role (`memberRoleValue`) and warns that saving applies it.
 */
export function memberRoleLabel(
  member:
    | { roleKey?: string | null; role_name?: string | null; role_is_default?: boolean | null }
    | null
    | undefined,
  fallback: string | null | undefined,
  roles?: readonly Pick<RbacRole, 'key' | 'name'>[] | null
): string {
  const sent = member?.role_name?.trim()
  const known = roles && member?.roleKey ? roleNameOf(roles, member.roleKey)?.trim() : undefined
  const name = sent || known || fallback?.trim() || LAST_RESORT_ROLE_LABEL
  return member?.role_is_default === true ? `${name} ${DEFAULT_ROLE_MARKER}` : name
}

/**
 * The roles an inviter with `roles.assign` may pick: every role but Owner whose permissions they
 * all hold (the server and cloud refuse the rest), plus the company default, which anyone may
 * send. Order is the API's.
 */
export function assignableRoles(
  roles: readonly RbacRole[],
  actorPermissions: readonly string[],
  defaultRoleKey: string
): RbacRole[] {
  return roles.filter(
    (role) =>
      role.key !== OWNER_ROLE_KEY &&
      (role.key === defaultRoleKey || canAssignRole(actorPermissions, role))
  )
}

/**
 * The role an invite carries when the person can't pick one (no `roles.assign`): the company's
 * effective default. Cloud lets an inviter without `roles.assign` send the default role only, so
 * this keeps their invite working, custom default included. When the stored legacy string stands
 * for the same role as the key, that string is sent as stored: a default of `manager` must stay
 * `manager`, because while RBAC is off the cloud's member-management checks accept only `manager`
 * and `admin` literally. `defaultRoleKey` absent (the roles list did not load): the legacy string
 * as before, else Member.
 */
export function roleWithoutPicker(
  companyDefaultRole: string | undefined | null,
  defaultRoleKey?: string | null
): string {
  const legacy =
    companyDefaultRole && LEGACY_INVITE_ROLES.has(companyDefaultRole) ? companyDefaultRole : undefined
  if (!defaultRoleKey) return legacy ?? CompanyUserRole.USER
  if (legacy && systemKeyOf(legacy) === defaultRoleKey) return legacy
  return roleSendValue(defaultRoleKey)
}

/**
 * What the company form holds as the default role, in the form a save sends: a custom role's key
 * as it is, Member and Admin as the legacy string cloud stored (a `manager` default stays
 * `manager`). Read from the settings the way `effectiveDefaultRoleKey` does, so a custom default
 * shows as that role and not as Member.
 */
export function companyDefaultRoleValue(
  company: Parameters<typeof effectiveDefaultRoleKey>[0]['company']
): string {
  const key = effectiveDefaultRoleKey({ company })
  if (!systemKeyOf(key)) return key
  const legacy = companyDefaultRole(company)
  return legacy && systemKeyOf(legacy) === key ? legacy : roleSendValue(key)
}

/**
 * A company's default role for new sign-ups. Cloud returns it inside `settings`
 * (`Company.settings.default_role`); the top-level field is only a fallback for older shapes.
 */
export function companyDefaultRole(
  company:
    { default_role?: unknown; settings?: { default_role?: unknown } | null } | null | undefined
): string | undefined {
  const value = company?.settings?.default_role ?? company?.default_role
  return typeof value === 'string' && value ? value : undefined
}

/** `Company.settings.default_role_key` as cloud returns it: a role key, or undefined. */
export function companyDefaultRoleKeyOf(
  company: { settings?: { default_role_key?: unknown } | null } | null | undefined
): string | undefined {
  const value = company?.settings?.default_role_key
  return typeof value === 'string' && value ? value : undefined
}

/**
 * The company form's `default_role` is sent only when the person changed it. A settings save
 * that resent it would overwrite a custom default role key that this form can't show.
 * `original` is the value the form was filled with.
 */
export function withoutUnchangedDefaultRole<T extends { default_role?: unknown }>(
  form: T,
  original: unknown
): T {
  // Compared as role keys, so re-picking the role the company already has (Member picked again
  // over a stored `user`) sends nothing, while Member over a custom default is a change.
  const unchanged =
    form.default_role === original ||
    (typeof form.default_role === 'string' &&
      typeof original === 'string' &&
      roleKeyOf(form.default_role) === roleKeyOf(original))
  if (!unchanged) return form
  const { default_role: _unchanged, ...rest } = form
  return rest as T
}

export interface RolePickerChoice {
  key: string
  name: string
  /** Shown so the select has a value, but not pickable. */
  disabled: boolean
}

/**
 * The entries of a role picker, named by the roles API. `roles` is the company's list; when it is
 * empty (a create form has no company, or the list did not load) the catalogue's system roles
 * stand in, under the names the catalogue gives. Owner is never pickable. Only roles whose keys
 * the person holds are offered, plus `alwaysAllowedKey` (the Member default, which cloud lets
 * anyone with `roles.assign` set). A current role that is not offered (a custom role they lack
 * the keys of, or Owner) stays in the list, by its name, disabled.
 */
export function rolePickerChoices(input: {
  roles: readonly RbacRole[]
  catalogueRoles?: readonly RbacSystemRole[]
  actorPermissions: readonly string[]
  currentKey?: string | null
  alwaysAllowedKey?: string
}): RolePickerChoice[] {
  const source: readonly RbacRole[] = input.roles.length
    ? input.roles
    : (input.catalogueRoles ?? []).map((r) => ({
        ...r,
        isSystem: true,
        scope: 'global' as const,
      }))
  const offered = assignableRoles(source, input.actorPermissions, input.alwaysAllowedKey ?? '')
  const choices = offered.map<RolePickerChoice>((r) => ({ key: r.key, name: r.name, disabled: false }))
  const current = input.currentKey
  if (current && !choices.some((c) => c.key === current)) {
    choices.unshift({ key: current, name: roleNameOf(source, current) ?? current, disabled: true })
  }
  return choices
}
